import { useState } from 'react';
import { PiArrowCounterClockwise, PiBuildingLight } from 'react-icons/pi';
import type { PropertyCodeDisplayPreference } from '../../types/property-code-setting.type';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import { InputFieldSkeleton } from 'src/modules/shared/components/atoms/inputs/input-field-skeleton';
import { Text } from 'src/modules/shared/components/atoms/text';
import { InlineFormSection } from 'src/modules/shared/components/molecules/inline-form-section/inline-form-section';
import { InlineSaveFeedback } from 'src/modules/shared/components/molecules/inline-save-feedback/inline-save-feedback';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { SettingPermission } from 'src/modules/shared/types/permissions.type';
import { usePropertyCodeSetting } from '../../hooks/use-property-code-setting';

/** Persist each tenant display-code selection immediately and refresh property views. */
export function PropertyCodeSettingsSection() {
  const { t } = useI18n();
  const { hasPermission: canRead } = useHasPermissions(SettingPermission.Read);
  const { hasPermission: canManage } = useHasPermissions(
    SettingPermission.ManageTenant
  );
  const { setting, loading, error, mutation, hasInvalidValue } =
    usePropertyCodeSetting(canRead);
  const [draft, setDraft] = useState<PropertyCodeDisplayPreference | null>(
    null
  );
  const value = draft ?? setting;
  const disabled =
    !canManage || loading || Boolean(error) || mutation.isPending;
  const save = (next: PropertyCodeDisplayPreference) => {
    if (disabled) return;
    setDraft(next);
    mutation.mutate(next, { onSuccess: () => setDraft(null) });
  };
  if (!canRead) return <Text>{t('settings.properties.noPermission')}</Text>;
  return (
    <InlineFormSection
      title={t('settings.properties.code.label')}
      description={t('settings.properties.code.help')}
      icon={<PiBuildingLight className="h-5 w-5" aria-hidden />}
      headerAction={
        canManage &&
        !loading &&
        (hasInvalidValue || setting !== 'comos') && (
          <Button
            size="sm"
            appearance="ghost"
            variant="neutral"
            disabled={disabled}
            onClick={() => save('comos')}
          >
            <PiArrowCounterClockwise className="h-3.5 w-3.5" aria-hidden />
            {t('propertyLinks.resetDefault')}
          </Button>
        )
      }
    >
      {!loading && hasInvalidValue && (
        <div role="alert">
          <Text>{t('settings.properties.code.invalid')}</Text>
        </div>
      )}
      <div aria-busy={loading}>
        {loading ? (
          <InputFieldSkeleton inputClassName="w-full" />
        ) : (
          <Select
            id="property-code-display"
            aria-label={t('settings.properties.code.label')}
            value={value}
            disabled={disabled}
            onChange={(event) => {
              const next = event.target.value;
              if (next === 'comos' || next === 'external') {
                if (next !== setting || hasInvalidValue) {
                  save(next);
                } else {
                  setDraft(null);
                  mutation.reset();
                }
              }
            }}
            options={[
              { value: 'comos', content: t('settings.properties.code.comos') },
              {
                value: 'external',
                content: t('settings.properties.code.external'),
              },
            ]}
          />
        )}
      </div>
      <InlineSaveFeedback
        saving={mutation.isPending}
        saved={mutation.isSuccess}
        error={mutation.error ? t('settings.properties.code.error') : undefined}
        onRetry={canManage ? () => save(value) : undefined}
      />
      {error && (
        <div role="alert">
          <Text>{t('settings.properties.code.error')}</Text>
        </div>
      )}
    </InlineFormSection>
  );
}
